import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { parse } from 'vscode/vscode/vs/base/common/json';
import { setProperty } from 'vscode/vscode/vs/base/common/jsonEdit';
import { Queue } from 'vscode/vscode/vs/base/common/async';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { WorkbenchState } from 'vscode/vscode/vs/platform/workspace/common/workspace';
import { IWorkspaceContextService } from 'vscode/vscode/vs/platform/workspace/common/workspace.service';
import { ITextFileService } from 'vscode/vscode/vs/workbench/services/textfile/common/textfiles.service';
import { TASKS_CONFIGURATION_KEY, LAUNCH_CONFIGURATION_KEY, TASKS_DEFAULT, FOLDER_SCOPES, USER_STANDALONE_CONFIGURATIONS, WORKSPACE_STANDALONE_CONFIGURATIONS, FOLDER_SETTINGS_PATH } from 'vscode/vscode/vs/workbench/services/configuration/common/configuration';
import { IWorkbenchConfigurationService } from 'vscode/vscode/vs/workbench/services/configuration/common/configuration.service';
import { FileOperationResult } from 'vscode/vscode/vs/platform/files/common/files';
import { IFileService } from 'vscode/vscode/vs/platform/files/common/files.service';
import { ITextModelService } from 'vscode/vscode/vs/editor/common/services/resolverService';
import { Extensions, OVERRIDE_PROPERTY_REGEX, ConfigurationScope, keyFromOverrideIdentifiers } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import 'vscode/vscode/vs/platform/notification/common/notification';
import { INotificationService } from 'vscode/vscode/vs/platform/notification/common/notification.service';
import { IPreferencesService } from 'vscode/vscode/vs/workbench/services/preferences/common/preferences.service';
import { IUriIdentityService } from 'vscode/vscode/vs/platform/uriIdentity/common/uriIdentity.service';
import { Range } from 'vscode/vscode/vs/editor/common/core/range';
import { EditOperation } from 'vscode/vscode/vs/editor/common/core/editOperation';
import { Selection } from 'vscode/vscode/vs/editor/common/core/selection';
import { IUserDataProfileService } from 'vscode/vscode/vs/workbench/services/userDataProfile/common/userDataProfile.service';
import { IUserDataProfilesService } from 'vscode/vscode/vs/platform/userDataProfile/common/userDataProfile.service';
import { ErrorNoTelemetry } from 'vscode/vscode/vs/base/common/errors';
import Severity$1 from 'vscode/vscode/vs/base/common/severity';

const _moduleId = "vs/workbench/services/configuration/common/configurationEditing";
var ConfigurationEditingErrorCode;
( ((function(ConfigurationEditingErrorCode) {
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_UNKNOWN_KEY"] = 0] = "ERROR_UNKNOWN_KEY";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_INVALID_WORKSPACE_CONFIGURATION_APPLICATION"] = 1] = "ERROR_INVALID_WORKSPACE_CONFIGURATION_APPLICATION";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_INVALID_WORKSPACE_CONFIGURATION_MACHINE"] = 2] = "ERROR_INVALID_WORKSPACE_CONFIGURATION_MACHINE";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_INVALID_FOLDER_CONFIGURATION"] = 3] = "ERROR_INVALID_FOLDER_CONFIGURATION";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_INVALID_USER_TARGET"] = 4] = "ERROR_INVALID_USER_TARGET";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_INVALID_WORKSPACE_TARGET"] = 5] = "ERROR_INVALID_WORKSPACE_TARGET";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_INVALID_FOLDER_TARGET"] = 6] = "ERROR_INVALID_FOLDER_TARGET";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_INVALID_RESOURCE_LANGUAGE_CONFIGURATION"] = 7] = "ERROR_INVALID_RESOURCE_LANGUAGE_CONFIGURATION";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_NO_WORKSPACE_OPENED"] = 8] = "ERROR_NO_WORKSPACE_OPENED";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_CONFIGURATION_FILE_DIRTY"] = 9] = "ERROR_CONFIGURATION_FILE_DIRTY";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_CONFIGURATION_FILE_MODIFIED_SINCE"] = 10] = "ERROR_CONFIGURATION_FILE_MODIFIED_SINCE";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_INVALID_CONFIGURATION"] = 11] = "ERROR_INVALID_CONFIGURATION";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_POLICY_CONFIGURATION"] = 12] = "ERROR_POLICY_CONFIGURATION";
    ConfigurationEditingErrorCode[ConfigurationEditingErrorCode["ERROR_INTERNAL"] = 13] = "ERROR_INTERNAL";
})(ConfigurationEditingErrorCode || (ConfigurationEditingErrorCode = {}))));
class ConfigurationEditingError extends ErrorNoTelemetry {
    constructor(message, code) {
        super(message);
        this.code = code;
    }
}
var EditableConfigurationTarget;
( ((function(EditableConfigurationTarget) {
    EditableConfigurationTarget[EditableConfigurationTarget["USER_LOCAL"] = 1] = "USER_LOCAL";
    EditableConfigurationTarget[EditableConfigurationTarget["USER_REMOTE"] = 2] = "USER_REMOTE";
    EditableConfigurationTarget[EditableConfigurationTarget["WORKSPACE"] = 3] = "WORKSPACE";
    EditableConfigurationTarget[EditableConfigurationTarget["WORKSPACE_FOLDER"] = 4] = "WORKSPACE_FOLDER";
})(EditableConfigurationTarget || (EditableConfigurationTarget = {}))));
let ConfigurationEditing = class ConfigurationEditing {
    constructor(remoteSettingsResource, configurationService, contextService, userDataProfileService, userDataProfilesService, fileService, textModelResolverService, textFileService, notificationService, preferencesService, editorService, uriIdentityService) {
        this.remoteSettingsResource = remoteSettingsResource;
        this.configurationService = configurationService;
        this.contextService = contextService;
        this.userDataProfileService = userDataProfileService;
        this.userDataProfilesService = userDataProfilesService;
        this.fileService = fileService;
        this.textModelResolverService = textModelResolverService;
        this.textFileService = textFileService;
        this.notificationService = notificationService;
        this.preferencesService = preferencesService;
        this.editorService = editorService;
        this.uriIdentityService = uriIdentityService;
        this.queue = ( (new Queue()));
    }
    async writeConfiguration(target, value, options = {}) {
        const operation = this.getConfigurationEditOperation(target, value, options.scopes || {});
        return this.queue.queue(async () => {
            try {
                await this.doWriteConfiguration(operation, options);
            }
            catch (error) {
                if (options.donotNotifyError) {
                    throw error;
                }
                await this.onError(error, operation, options.scopes);
            }
        });
    }
    async doWriteConfiguration(operation, options) {
        await this.validate(operation.target, operation, !options.handleDirtyFile, options.scopes || {});
        const resource = operation.resource;
        const reference = await this.resolveModelReference(resource);
        try {
            const formattingOptions = this.getFormattingOptions(reference.object.textEditorModel);
            await this.updateConfiguration(operation, reference.object.textEditorModel, formattingOptions, options);
        }
        finally {
            reference.dispose();
        }
    }
    async updateConfiguration(operation, model, formattingOptions, options) {
        if (this.hasParseErrors(model.getValue(), operation)) {
            throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_INVALID_CONFIGURATION, operation.target, operation);
        }
        if (this.textFileService.isDirty(model.uri) && options.handleDirtyFile) {
            switch (options.handleDirtyFile) {
                case 'save':
                    await this.save(model, operation);
                    break;
                case 'revert':
                    await this.textFileService.revert(model.uri);
                    break;
            }
        }
        const edit = this.getEdits(operation, model.getValue(), formattingOptions)[0];
        if (edit && this.applyEditsToBuffer(edit, model)) {
            await this.save(model, operation);
        }
    }
    async save(model, operation) {
        try {
            await this.textFileService.save(model.uri, { ignoreErrorHandler: true });
        }
        catch (error) {
            if (error.fileOperationResult === FileOperationResult.FILE_MODIFIED_SINCE) {
                throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_CONFIGURATION_FILE_MODIFIED_SINCE, operation.target, operation);
            }
            throw ( (new ConfigurationEditingError(localizeWithPath(
                _moduleId,
                0,
                "Error while writing to {0}. {1}",
                this.stringifyTarget(operation.target),
                error.message
            ), ConfigurationEditingErrorCode.ERROR_INTERNAL)));
        }
    }
    applyEditsToBuffer(edit, model) {
        const startPosition = model.getPositionAt(edit.offset);
        const endPosition = model.getPositionAt(edit.offset + edit.length);
        const range = ( (new Range(
            startPosition.lineNumber,
            startPosition.column,
            endPosition.lineNumber,
            endPosition.column
        )));
        const currentText = model.getValueInRange(range);
        if (edit.content !== currentText) {
            const editOperation = currentText ? EditOperation.replace(range, edit.content) : EditOperation.insert(startPosition, edit.content);
            model.pushEditOperations([( (new Selection(
                startPosition.lineNumber,
                startPosition.column,
                startPosition.lineNumber,
                startPosition.column
            )))], [editOperation], () => []);
            return true;
        }
        return false;
    }
    getEdits({ value, jsonPath }, modelContent, formattingOptions) {
        if (jsonPath.length) {
            return setProperty(modelContent, jsonPath, value, formattingOptions);
        }
        const content = JSON.stringify(value, null, formattingOptions.insertSpaces && formattingOptions.tabSize ? ' '.repeat(formattingOptions.tabSize) : '\t');
        return [{
                content,
                length: modelContent.length,
                offset: 0
            }];
    }
    getFormattingOptions(model) {
        const { insertSpaces, tabSize } = model.getOptions();
        const eol = model.getEOL();
        return { insertSpaces, tabSize, eol };
    }
    async onError(error, operation, scopes) {
        switch (error.code) {
            case ConfigurationEditingErrorCode.ERROR_INVALID_CONFIGURATION:
                this.onInvalidConfigurationError(error, operation);
                break;
            case ConfigurationEditingErrorCode.ERROR_CONFIGURATION_FILE_DIRTY:
                this.onConfigurationFileDirtyError(error, operation, scopes);
                break;
            case ConfigurationEditingErrorCode.ERROR_CONFIGURATION_FILE_MODIFIED_SINCE:
                return this.doWriteConfiguration(operation, { scopes, handleDirtyFile: 'revert' });
            default:
                this.notificationService.error(error.message);
        }
    }
    onInvalidConfigurationError(error, operation) {
        const openStandAloneConfigurationActionLabel = operation.workspaceStandAloneConfigurationKey === TASKS_CONFIGURATION_KEY ? ( localizeWithPath(_moduleId, 1, "Open Tasks Configuration"))
            : operation.workspaceStandAloneConfigurationKey === LAUNCH_CONFIGURATION_KEY ? ( localizeWithPath(_moduleId, 2, "Open Launch Configuration"))
                : null;
        if (openStandAloneConfigurationActionLabel) {
            this.notificationService.prompt(Severity$1.Error, error.message, [{
                    label: openStandAloneConfigurationActionLabel,
                    run: () => this.openFile(operation.resource)
                }]);
        }
        else {
            this.notificationService.prompt(Severity$1.Error, error.message, [{
                    label: ( localizeWithPath(_moduleId, 3, "Open Settings")),
                    run: () => this.openSettings(operation)
                }]);
        }
    }
    onConfigurationFileDirtyError(error, operation, scopes) {
        const openStandAloneConfigurationActionLabel = operation.workspaceStandAloneConfigurationKey === TASKS_CONFIGURATION_KEY ? ( localizeWithPath(_moduleId, 1, "Open Tasks Configuration"))
            : operation.workspaceStandAloneConfigurationKey === LAUNCH_CONFIGURATION_KEY ? ( localizeWithPath(_moduleId, 2, "Open Launch Configuration"))
                : null;
        if (openStandAloneConfigurationActionLabel) {
            this.notificationService.prompt(Severity$1.Error, error.message, [{
                    label: ( localizeWithPath(_moduleId, 4, "Save and Retry")),
                    run: () => {
                        const key = operation.key ? `${operation.workspaceStandAloneConfigurationKey}.${operation.key}` : operation.workspaceStandAloneConfigurationKey;
                        this.writeConfiguration(operation.target, { key, value: operation.value }, { handleDirtyFile: 'save', scopes });
                    }
                },
                {
                    label: openStandAloneConfigurationActionLabel,
                    run: () => this.openFile(operation.resource)
                }]);
        }
        else {
            this.notificationService.prompt(Severity$1.Error, error.message, [{
                    label: ( localizeWithPath(_moduleId, 4, "Save and Retry")),
                    run: () => this.writeConfiguration(operation.target, { key: operation.key, value: operation.value }, { handleDirtyFile: 'save', scopes })
                },
                {
                    label: ( localizeWithPath(_moduleId, 3, "Open Settings")),
                    run: () => this.openSettings(operation)
                }]);
        }
    }
    openSettings(operation) {
        const options = { jsonEditor: true };
        switch (operation.target) {
            case EditableConfigurationTarget.USER_LOCAL:
                this.preferencesService.openUserSettings(options);
                break;
            case EditableConfigurationTarget.USER_REMOTE:
                this.preferencesService.openRemoteSettings(options);
                break;
            case EditableConfigurationTarget.WORKSPACE:
                this.preferencesService.openWorkspaceSettings(options);
                break;
            case EditableConfigurationTarget.WORKSPACE_FOLDER:
                if (operation.resource) {
                    const workspaceFolder = this.contextService.getWorkspaceFolder(operation.resource);
                    if (workspaceFolder) {
                        this.preferencesService.openFolderSettings({ folderUri: workspaceFolder.uri, jsonEditor: true });
                    }
                }
                break;
        }
    }
    openFile(resource) {
        this.editorService.openEditor({ resource, options: { pinned: true } });
    }
    toConfigurationEditingError(code, target, operation) {
        const message = this.toErrorMessage(code, target, operation);
        return (
             (new ConfigurationEditingError(message, code))
        );
    }
    toErrorMessage(error, target, operation) {
        switch (error) {
            case ConfigurationEditingErrorCode.ERROR_POLICY_CONFIGURATION: return ( localizeWithPath(
                _moduleId,
                5,
                "Unable to write {0} because it is configured in system policy.",
                operation.key
            ));
            case ConfigurationEditingErrorCode.ERROR_UNKNOWN_KEY: return ( localizeWithPath(
                _moduleId,
                6,
                "Unable to write to {0} because {1} is not a registered configuration.",
                this.stringifyTarget(target),
                operation.key
            ));
            case ConfigurationEditingErrorCode.ERROR_INVALID_WORKSPACE_CONFIGURATION_APPLICATION: return ( localizeWithPath(
                _moduleId,
                7,
                "Unable to write {0} to Workspace Settings. This setting can be written only into User settings.",
                operation.key
            ));
            case ConfigurationEditingErrorCode.ERROR_INVALID_WORKSPACE_CONFIGURATION_MACHINE: return ( localizeWithPath(
                _moduleId,
                8,
                "Unable to write {0} to Workspace Settings. This setting can be written only into User settings.",
                operation.key
            ));
            case ConfigurationEditingErrorCode.ERROR_INVALID_FOLDER_CONFIGURATION: return ( localizeWithPath(
                _moduleId,
                9,
                "Unable to write to Folder Settings because {0} does not support the folder resource scope.",
                operation.key
            ));
            case ConfigurationEditingErrorCode.ERROR_INVALID_USER_TARGET: return ( localizeWithPath(
                _moduleId,
                10,
                "Unable to write to User Settings because {0} does not support for global scope.",
                operation.key
            ));
            case ConfigurationEditingErrorCode.ERROR_INVALID_WORKSPACE_TARGET: return ( localizeWithPath(
                _moduleId,
                11,
                "Unable to write to Workspace Settings because {0} does not support for workspace scope in a multi folder workspace.",
                operation.key
            ));
            case ConfigurationEditingErrorCode.ERROR_INVALID_FOLDER_TARGET: return ( localizeWithPath(
                _moduleId,
                12,
                "Unable to write to Folder Settings because no resource is provided."
            ));
            case ConfigurationEditingErrorCode.ERROR_INVALID_RESOURCE_LANGUAGE_CONFIGURATION: return ( localizeWithPath(
                _moduleId,
                13,
                "Unable to write to Language Settings because {0} is not a resource language setting.",
                operation.key
            ));
            case ConfigurationEditingErrorCode.ERROR_NO_WORKSPACE_OPENED: return ( localizeWithPath(
                _moduleId,
                14,
                "Unable to write to {0} because no workspace is opened. Please open a workspace first and try again.",
                this.stringifyTarget(target)
            ));
            case ConfigurationEditingErrorCode.ERROR_INVALID_CONFIGURATION: {
                if (operation.workspaceStandAloneConfigurationKey === TASKS_CONFIGURATION_KEY) {
                    return ( localizeWithPath(
                        _moduleId,
                        15,
                        "Unable to write into the tasks configuration file. Please open it to correct errors/warnings in it and try again."
                    ));
                }
                if (operation.workspaceStandAloneConfigurationKey === LAUNCH_CONFIGURATION_KEY) {
                    return ( localizeWithPath(
                        _moduleId,
                        16,
                        "Unable to write into the launch configuration file. Please open it to correct errors/warnings in it and try again."
                    ));
                }
                switch (target) {
                    case EditableConfigurationTarget.USER_LOCAL:
                        return ( localizeWithPath(
                            _moduleId,
                            17,
                            "Unable to write into user settings. Please open the user settings to correct errors/warnings in it and try again."
                        ));
                    case EditableConfigurationTarget.USER_REMOTE:
                        return ( localizeWithPath(
                            _moduleId,
                            18,
                            "Unable to write into remote user settings. Please open the remote user settings to correct errors/warnings in it and try again."
                        ));
                    case EditableConfigurationTarget.WORKSPACE:
                        return ( localizeWithPath(
                            _moduleId,
                            19,
                            "Unable to write into workspace settings. Please open the workspace settings to correct errors/warnings in the file and try again."
                        ));
                    case EditableConfigurationTarget.WORKSPACE_FOLDER: {
                        let workspaceFolderName = '<<unknown>>';
                        if (operation.resource) {
                            const folder = this.contextService.getWorkspaceFolder(operation.resource);
                            if (folder) {
                                workspaceFolderName = folder.name;
                            }
                        }
                        return ( localizeWithPath(
                            _moduleId,
                            20,
                            "Unable to write into folder settings. Please open the '{0}' folder settings to correct errors/warnings in it and try again.",
                            workspaceFolderName
                        ));
                    }
                    default:
                        return '';
                }
            }
            case ConfigurationEditingErrorCode.ERROR_CONFIGURATION_FILE_DIRTY: {
                if (operation.workspaceStandAloneConfigurationKey === TASKS_CONFIGURATION_KEY) {
                    return ( localizeWithPath(
                        _moduleId,
                        21,
                        "Unable to write into tasks configuration file because the file has unsaved changes. Please save it first and then try again."
                    ));
                }
                if (operation.workspaceStandAloneConfigurationKey === LAUNCH_CONFIGURATION_KEY) {
                    return ( localizeWithPath(
                        _moduleId,
                        22,
                        "Unable to write into launch configuration file because the file has unsaved changes. Please save it first and then try again."
                    ));
                }
                switch (target) {
                    case EditableConfigurationTarget.USER_LOCAL:
                        return ( localizeWithPath(
                            _moduleId,
                            23,
                            "Unable to write into user settings because the file has unsaved changes. Please save the user settings file first and then try again."
                        ));
                    case EditableConfigurationTarget.USER_REMOTE:
                        return ( localizeWithPath(
                            _moduleId,
                            24,
                            "Unable to write into remote user settings because the file has unsaved changes. Please save the remote user settings file first and then try again."
                        ));
                    case EditableConfigurationTarget.WORKSPACE:
                        return ( localizeWithPath(
                            _moduleId,
                            25,
                            "Unable to write into workspace settings because the file has unsaved changes. Please save the workspace settings file first and then try again."
                        ));
                    case EditableConfigurationTarget.WORKSPACE_FOLDER: {
                        let workspaceFolderName = '<<unknown>>';
                        if (operation.resource) {
                            const folder = this.contextService.getWorkspaceFolder(operation.resource);
                            if (folder) {
                                workspaceFolderName = folder.name;
                            }
                        }
                        return ( localizeWithPath(
                            _moduleId,
                            26,
                            "Unable to write into folder settings because the file has unsaved changes. Please save the '{0}' folder settings file first and then try again.",
                            workspaceFolderName
                        ));
                    }
                    default:
                        return '';
                }
            }
            case ConfigurationEditingErrorCode.ERROR_CONFIGURATION_FILE_MODIFIED_SINCE:
                if (operation.workspaceStandAloneConfigurationKey === TASKS_CONFIGURATION_KEY) {
                    return ( localizeWithPath(
                        _moduleId,
                        27,
                        "Unable to write into tasks configuration file because the content of the file is newer."
                    ));
                }
                if (operation.workspaceStandAloneConfigurationKey === LAUNCH_CONFIGURATION_KEY) {
                    return ( localizeWithPath(
                        _moduleId,
                        28,
                        "Unable to write into launch configuration file because the content of the file is newer."
                    ));
                }
                switch (target) {
                    case EditableConfigurationTarget.USER_LOCAL:
                        return ( localizeWithPath(
                            _moduleId,
                            29,
                            "Unable to write into user settings because the content of the file is newer."
                        ));
                    case EditableConfigurationTarget.USER_REMOTE:
                        return ( localizeWithPath(
                            _moduleId,
                            30,
                            "Unable to write into remote user settings because the content of the file is newer."
                        ));
                    case EditableConfigurationTarget.WORKSPACE:
                        return ( localizeWithPath(
                            _moduleId,
                            31,
                            "Unable to write into workspace settings because the content of the file is newer."
                        ));
                    case EditableConfigurationTarget.WORKSPACE_FOLDER:
                        return ( localizeWithPath(
                            _moduleId,
                            32,
                            "Unable to write into folder settings because the content of the file is newer."
                        ));
                }
            case ConfigurationEditingErrorCode.ERROR_INTERNAL: return ( localizeWithPath(
                _moduleId,
                33,
                "Unable to write to {0} because of an internal error.",
                this.stringifyTarget(target)
            ));
        }
    }
    stringifyTarget(target) {
        switch (target) {
            case EditableConfigurationTarget.USER_LOCAL:
                return ( localizeWithPath(_moduleId, 34, "User Settings"));
            case EditableConfigurationTarget.USER_REMOTE:
                return ( localizeWithPath(_moduleId, 35, "Remote User Settings"));
            case EditableConfigurationTarget.WORKSPACE:
                return ( localizeWithPath(_moduleId, 36, "Workspace Settings"));
            case EditableConfigurationTarget.WORKSPACE_FOLDER:
                return ( localizeWithPath(_moduleId, 37, "Folder Settings"));
            default:
                return '';
        }
    }
    defaultResourceValue(resource) {
        const basename = this.uriIdentityService.extUri.basename(resource);
        const configurationValue = basename.substr(0, basename.length - this.uriIdentityService.extUri.extname(resource).length);
        switch (configurationValue) {
            case TASKS_CONFIGURATION_KEY: return TASKS_DEFAULT;
            default: return '{}';
        }
    }
    async resolveModelReference(resource) {
        const exists = await this.fileService.exists(resource);
        if (!exists) {
            await this.textFileService.write(resource, this.defaultResourceValue(resource), { encoding: 'utf8' });
        }
        return this.textModelResolverService.createModelReference(resource);
    }
    hasParseErrors(content, operation) {
        if (operation.workspaceStandAloneConfigurationKey && !operation.key) {
            return false;
        }
        const parseErrors = [];
        parse(content, parseErrors, { allowTrailingComma: true, allowEmptyContent: true });
        return parseErrors.length > 0;
    }
    async validate(target, operation, checkDirty, overrides) {
        if (this.configurationService.inspect(operation.key).policyValue !== undefined) {
            throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_POLICY_CONFIGURATION, target, operation);
        }
        const configurationProperties = ( (Registry.as(Extensions.Configuration))).getConfigurationProperties();
        const configurationScope = configurationProperties[operation.key]?.scope;
        if (!operation.workspaceStandAloneConfigurationKey) {
            const validKeys = ( (this.configurationService.keys())).default;
            if (validKeys.indexOf(operation.key) < 0 && !OVERRIDE_PROPERTY_REGEX.test(operation.key) && operation.value !== undefined) {
                throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_UNKNOWN_KEY, target, operation);
            }
        }
        if (operation.workspaceStandAloneConfigurationKey) {
            if ((operation.workspaceStandAloneConfigurationKey !== TASKS_CONFIGURATION_KEY) && (target === EditableConfigurationTarget.USER_LOCAL || target === EditableConfigurationTarget.USER_REMOTE)) {
                throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_INVALID_USER_TARGET, target, operation);
            }
        }
        if ((target === EditableConfigurationTarget.WORKSPACE || target === EditableConfigurationTarget.WORKSPACE_FOLDER) && this.contextService.getWorkbenchState() === WorkbenchState.EMPTY) {
            throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_NO_WORKSPACE_OPENED, target, operation);
        }
        if (target === EditableConfigurationTarget.WORKSPACE) {
            if (!operation.workspaceStandAloneConfigurationKey && !OVERRIDE_PROPERTY_REGEX.test(operation.key)) {
                if (configurationScope === ConfigurationScope.APPLICATION) {
                    throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_INVALID_WORKSPACE_CONFIGURATION_APPLICATION, target, operation);
                }
                if (configurationScope === ConfigurationScope.MACHINE) {
                    throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_INVALID_WORKSPACE_CONFIGURATION_MACHINE, target, operation);
                }
            }
        }
        if (target === EditableConfigurationTarget.WORKSPACE_FOLDER) {
            if (!operation.resource) {
                throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_INVALID_FOLDER_TARGET, target, operation);
            }
            if (!operation.workspaceStandAloneConfigurationKey && !OVERRIDE_PROPERTY_REGEX.test(operation.key)) {
                if (configurationScope !== undefined && !FOLDER_SCOPES.includes(configurationScope)) {
                    throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_INVALID_FOLDER_CONFIGURATION, target, operation);
                }
            }
        }
        if (overrides.overrideIdentifiers?.length) {
            if (configurationScope !== ConfigurationScope.LANGUAGE_OVERRIDABLE) {
                throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_INVALID_RESOURCE_LANGUAGE_CONFIGURATION, target, operation);
            }
        }
        if (!operation.resource) {
            throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_INVALID_FOLDER_TARGET, target, operation);
        }
        if (checkDirty && this.textFileService.isDirty(operation.resource)) {
            throw this.toConfigurationEditingError(ConfigurationEditingErrorCode.ERROR_CONFIGURATION_FILE_DIRTY, target, operation);
        }
    }
    getConfigurationEditOperation(target, config, overrides) {
        if (config.key) {
            const standaloneConfigurationMap = target === EditableConfigurationTarget.USER_LOCAL ? USER_STANDALONE_CONFIGURATIONS : WORKSPACE_STANDALONE_CONFIGURATIONS;
            const standaloneConfigurationKeys = ( (Object.keys(standaloneConfigurationMap)));
            for (const key of standaloneConfigurationKeys) {
                const resource = this.getConfigurationFileResource(target, key, standaloneConfigurationMap[key], overrides.resource, undefined);
                if (config.key === key) {
                    const jsonPath = this.isWorkspaceConfigurationResource(resource) ? [key] : [];
                    return { key: jsonPath[jsonPath.length - 1], jsonPath, value: config.value, resource: resource ?? undefined, workspaceStandAloneConfigurationKey: key, target };
                }
                const keyPrefix = `${key}.`;
                if (config.key.indexOf(keyPrefix) === 0) {
                    const jsonPath = this.isWorkspaceConfigurationResource(resource) ? [key, config.key.substr(keyPrefix.length)] : [config.key.substr(keyPrefix.length)];
                    return { key: jsonPath[jsonPath.length - 1], jsonPath, value: config.value, resource: resource ?? undefined, workspaceStandAloneConfigurationKey: key, target };
                }
            }
        }
        const key = config.key;
        const configurationProperties = ( (Registry.as(Extensions.Configuration))).getConfigurationProperties();
        const configurationScope = configurationProperties[key]?.scope;
        let jsonPath = overrides.overrideIdentifiers?.length ? [keyFromOverrideIdentifiers(overrides.overrideIdentifiers), key] : [key];
        if (target === EditableConfigurationTarget.USER_LOCAL || target === EditableConfigurationTarget.USER_REMOTE) {
            return { key, jsonPath, value: config.value, resource: this.getConfigurationFileResource(target, key, '', null, configurationScope) ?? undefined, target };
        }
        const resource = this.getConfigurationFileResource(target, key, FOLDER_SETTINGS_PATH, overrides.resource, configurationScope);
        if (this.isWorkspaceConfigurationResource(resource)) {
            jsonPath = ['settings', ...jsonPath];
        }
        return { key, jsonPath, value: config.value, resource: resource ?? undefined, target };
    }
    isWorkspaceConfigurationResource(resource) {
        const workspace = this.contextService.getWorkspace();
        return !!(workspace.configuration && resource && workspace.configuration.fsPath === resource.fsPath);
    }
    getConfigurationFileResource(target, key, relativePath, resource, scope) {
        if (target === EditableConfigurationTarget.USER_LOCAL) {
            if (key === TASKS_CONFIGURATION_KEY) {
                return this.userDataProfileService.currentProfile.tasksResource;
            }
            else {
                if (!this.userDataProfileService.currentProfile.isDefault && this.configurationService.isSettingAppliedForAllProfiles(key)) {
                    return this.userDataProfilesService.defaultProfile.settingsResource;
                }
                return this.userDataProfileService.currentProfile.settingsResource;
            }
        }
        if (target === EditableConfigurationTarget.USER_REMOTE) {
            return this.remoteSettingsResource;
        }
        const workbenchState = this.contextService.getWorkbenchState();
        if (workbenchState !== WorkbenchState.EMPTY) {
            const workspace = this.contextService.getWorkspace();
            if (target === EditableConfigurationTarget.WORKSPACE) {
                if (workbenchState === WorkbenchState.WORKSPACE) {
                    return workspace.configuration ?? null;
                }
                if (workbenchState === WorkbenchState.FOLDER) {
                    return workspace.folders[0].toResource(relativePath);
                }
            }
            if (target === EditableConfigurationTarget.WORKSPACE_FOLDER) {
                if (resource) {
                    const folder = this.contextService.getWorkspaceFolder(resource);
                    if (folder) {
                        return folder.toResource(relativePath);
                    }
                }
            }
        }
        return null;
    }
};
ConfigurationEditing = ( (__decorate([
    ( (__param(1, IWorkbenchConfigurationService))),
    ( (__param(2, IWorkspaceContextService))),
    ( (__param(3, IUserDataProfileService))),
    ( (__param(4, IUserDataProfilesService))),
    ( (__param(5, IFileService))),
    ( (__param(6, ITextModelService))),
    ( (__param(7, ITextFileService))),
    ( (__param(8, INotificationService))),
    ( (__param(9, IPreferencesService))),
    ( (__param(10, IEditorService))),
    ( (__param(11, IUriIdentityService)))
], ConfigurationEditing)));

export { ConfigurationEditing, ConfigurationEditingError, ConfigurationEditingErrorCode, EditableConfigurationTarget };
